import React, { useState } from "react";
import styled from "styled-components";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaPaperPlane, FaCheckCircle } from "react-icons/fa";
import AnimateOnScroll from "../AnimateOnScroll";
import { submitContactMessage } from "../../services/supportApi";
// Assets
import ContactImg1 from "../../assets/img/contact-1.png";
import ContactImg2 from "../../assets/img/contact-2.png";
import ContactImg3 from "../../assets/img/contact-3.png";

const INQUIRY_LABELS = {
  general: "General Inquiry",
  admission: "Admission Information",
  scholarship: "Scholarship Information",
  program: "Program Details",
  partnership: "Partnership",
  other: "Other",
};

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitError('');
    setIsSubmitting(true);

    try {
      const res = await submitContactMessage({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        inquiry_type: INQUIRY_LABELS[formData.inquiryType] || formData.inquiryType,
        subject: formData.subject,
        message: formData.message,
      });
      setSuccessMessage(res.message || "We'll get back to you within 24 hours.");
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        inquiryType: 'general'
      });
      setTimeout(() => {
        setIsSubmitted(false);
        setSuccessMessage('');
      }, 5000);
    } catch (err) {
      setSubmitError(err.message || err.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Wrapper id="contact">
        <div className="container">
        <AnimateOnScroll animation="fadeUp" delay={0} duration={800}>
          <HeaderInfo>
            <h1 className="font40 extraBold">Let's Get In Touch</h1>
            <p className="font16">
              Ready to start your academic journey with AFRIQ? We're here to help you succeed. 
              <br />
              Reach out to us for personalized guidance, program information, or any questions you may have.
            </p>
          </HeaderInfo>
        </AnimateOnScroll>

        <ContactContent>
          <ContactInfo>
            <AnimateOnScroll animation="fadeRight" delay={200} duration={800}>
              <InfoCard>
                <InfoIcon>
                  <FaEnvelope />
                </InfoIcon>
                <InfoContent>
                  <h3 className="font18 extraBold">Email Us</h3>
                  <p className="font14">info@afriqcm.com</p>
                </InfoContent>
              </InfoCard>
            </AnimateOnScroll>

            <AnimateOnScroll animation="fadeRight" delay={400} duration={800}>
              <InfoCard>
                <InfoIcon>
                  <FaPhone />
                </InfoIcon>
                <InfoContent>
                  <h3 className="font18 extraBold">Call Us</h3>
                  <p className="font14">+237 679235785</p>
                  <p className="font14">+237 679052039</p>
                  <p className="font14">+237 676282853</p>
                  <p className="font14">+237 670 45 06 90</p>
                  <p className="font14">+237 676411380</p>
                </InfoContent>
              </InfoCard>
            </AnimateOnScroll>

            <AnimateOnScroll animation="fadeRight" delay={600} duration={800}>
              <InfoCard>
                <InfoIcon>
                  <FaMapMarkerAlt />
                </InfoIcon>
                <InfoContent>
                  <h3 className="font18 extraBold">Visit Us</h3>
                  <p className="font14">Commercial Avenue</p>
                  <p className="font14">Bamenda, Cameroon</p>
                </InfoContent>
              </InfoCard>
            </AnimateOnScroll>

            <AnimateOnScroll animation="fadeRight" delay={800} duration={800}>
              <InfoCard>
                <InfoIcon>
                  <FaClock />
                </InfoIcon>
                <InfoContent>
                  <h3 className="font18 extraBold">Office Hours</h3>
                  <p className="font14">Mon - Fri: 8:00 AM - 6:00 PM</p>
                  <p className="font14">Sat: 9:00 AM - 2:00 PM</p>
                </InfoContent>
              </InfoCard>
            </AnimateOnScroll>
          </ContactInfo>

          <ContactForm>
            <AnimateOnScroll animation="fadeLeft" delay={200} duration={800}>
              <FormCard>
                <FormHeader>
                  <h2 className="font25 extraBold">Send Us a Message</h2>
                  <p className="font14">We'll get back to you within 24 hours</p>
                </FormHeader>

                {isSubmitted ? (
                  <SuccessMessage>
                    <FaCheckCircle />
                    <h3 className="font20 extraBold">Message Sent Successfully!</h3>
                    <p className="font14">{successMessage}</p>
                  </SuccessMessage>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    {submitError && <SubmitError>{submitError}</SubmitError>}
                    <FormRow>
                      <FormGroup>
                        <Label htmlFor="firstName" className="font14 semiBold">
                          First Name *
                        </Label>
                        <Input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className={errors.firstName ? 'error' : ''}
                          placeholder="Enter your first name"
                        />
                        {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="lastName" className="font14 semiBold">
                          Last Name *
                        </Label>
                        <Input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className={errors.lastName ? 'error' : ''}
                          placeholder="Enter your last name"
                        />
                        {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
                      </FormGroup>
                    </FormRow>

                    <FormRow>
                      <FormGroup>
                        <Label htmlFor="email" className="font14 semiBold">
                          Email Address *
                        </Label>
                        <Input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={errors.email ? 'error' : ''}
                          placeholder="Enter your email address"
                        />
                        {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="phone" className="font14 semiBold">
                          Phone Number
                        </Label>
                        <Input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="Enter your phone number"
                        />
                      </FormGroup>
                    </FormRow>

                    <FormGroup>
                      <Label htmlFor="inquiryType" className="font14 semiBold">
                        Inquiry Type
                      </Label>
                      <Select
                        id="inquiryType"
                        name="inquiryType"
                        value={formData.inquiryType}
                        onChange={handleInputChange}
                      >
                        <option value="general">General Inquiry</option>
                        <option value="admission">Admission Information</option>
                        <option value="scholarship">Scholarship Information</option>
                        <option value="program">Program Details</option>
                        <option value="partnership">Partnership</option>
                        <option value="other">Other</option>
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="subject" className="font14 semiBold">
                        Subject *
                      </Label>
                      <Input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className={errors.subject ? 'error' : ''}
                        placeholder="What is this about?"
                      />
                      {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="message" className="font14 semiBold">
                        Message *
                      </Label>
                      <TextArea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className={errors.message ? 'error' : ''}
                        placeholder="Tell us more about your inquiry..."
                        rows="5"
                      />
                      {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
                    </FormGroup>

                    <SubmitButton type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Spinner />
                          Sending Message...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane />
                          Send Message
                        </>
                      )}
                    </SubmitButton>
              </Form>
                )}
              </FormCard>
            </AnimateOnScroll>
          </ContactForm>
        </ContactContent>

        <OfficeImages>
          <AnimateOnScroll animation="fadeUp" delay={0} duration={800}>
            <ImageCard>
              <img src={ContactImg1} alt="AFRIQ Office" />
              <ImageOverlay>
                <h4 className="font16 extraBold">Main Office</h4>
                <p className="font13">Commercial Avenue</p>
              </ImageOverlay>
            </ImageCard>
          </AnimateOnScroll>
          
          <AnimateOnScroll animation="fadeUp" delay={200} duration={800}>
            <ImageCard>
              <img src={ContactImg2} alt="AFRIQ Classroom" />
              <ImageOverlay>
                <h4 className="font16 extraBold">Study Center</h4>
                <p className="font13">Modern Learning Environment</p>
              </ImageOverlay>
            </ImageCard>
          </AnimateOnScroll>
          
          <AnimateOnScroll animation="fadeUp" delay={400} duration={800}>
            <ImageCard>
              <img src={ContactImg3} alt="AFRIQ Library" />
              <ImageOverlay>
                <h4 className="font16 extraBold">Resource Center</h4>
                <p className="font13">Comprehensive Study Materials</p>
              </ImageOverlay>
            </ImageCard>
          </AnimateOnScroll>
        </OfficeImages>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.section`
  width: 100%;
  padding: 120px 0;
  background: #f8f8ff;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
  
  @media (max-width: 480px) {
    padding: 60px 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 80%, rgba(75, 46, 131, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(221, 166, 31, 0.03) 0%, transparent 50%);
    z-index: 1;
  }
`;

const HeaderInfo = styled.div`
  margin-bottom: 60px;
  text-align: center;
  position: relative;
  z-index: 2;
  
  h1 {
    margin-bottom: 15px;
    color: #4b2e83;
    font-size: clamp(1.75rem, 4vw, 2.5rem);
    line-height: 1.3;
  }
  
  p {
    color: #000000;
    line-height: 1.7;
    max-width: 700px;
    margin: 0 auto;
    font-size: clamp(0.9rem, 2vw, 0.938rem);
  }
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  margin-bottom: 60px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
    margin-bottom: 50px;
  }
  
  @media (max-width: 768px) {
    gap: 30px;
    margin-bottom: 40px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoCard = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 22px;
  background: #ffffff;
  backdrop-filter: blur(20px);
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(75, 46, 131, 0.15);
  border: 1px solid rgba(75, 46, 131, 0.2);
  transition: all 0.3s ease;
  color: #000000;
  
  @media (max-width: 768px) {
    padding: 20px;
    gap: 15px;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 18px;
    gap: 12px;
    border-radius: 10px;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }
`;

const InfoIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #f8f8ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b2e83;
  font-size: 20px;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
  
  h3 {
    color: #4b2e83;
    margin-bottom: 8px;
  }
  
  p {
    color: #000000;
    margin: 2px 0;
    line-height: 1.4;
  }
`;

const ContactForm = styled.div`
  position: relative;
`;

const FormCard = styled.div`
  background: #ffffff;
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 35px;
  box-shadow: 0 8px 32px rgba(75, 46, 131, 0.15);
  border: 1px solid rgba(75, 46, 131, 0.2);
  transition: all 0.3s ease;
  color: #000000;
  
  @media (max-width: 768px) {
    padding: 30px 25px;
    border-radius: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 25px 20px;
    border-radius: 12px;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }
`;

const FormHeader = styled.div`
  margin-bottom: 30px;
    text-align: center;
  
  h2 {
    color: #4b2e83;
    margin-bottom: 8px;
  }
  
  p {
    color: #000000;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #4b2e83;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 15px 20px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #ffffff;
  color: #000000;
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    box-shadow: 0 0 0 3px rgba(75, 46, 131, 0.1);
    background: #ffffff;
    color: #000000;
  }
  
  &::placeholder {
    color: #999;
  }
  
  &.error {
    border-color: #ff6b6b;
    background: #ffffff;
    color: #000000;
  }
`;

const Select = styled.select`
  padding: 15px 20px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #ffffff;
  cursor: pointer;
  color: #000000;
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    box-shadow: 0 0 0 3px rgba(75, 46, 131, 0.1);
    background: #ffffff;
    color: #000000;
  }
`;

const TextArea = styled.textarea`
  padding: 15px 20px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #ffffff;
  resize: vertical;
  min-height: 120px;
  color: #000000;
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    box-shadow: 0 0 0 3px rgba(75, 46, 131, 0.1);
    background: #ffffff;
    color: #000000;
  }
  
  &::placeholder {
    color: #999;
  }
  
  &.error {
    border-color: #4b2e83;
    background: #ffffff;
    color: #000000;
  }
`;

const ErrorMessage = styled.span`
  color: #ff6b6b;
  font-size: 12px;
  font-weight: 500;
`;

const SubmitError = styled.p`
  margin: 0 0 8px;
  padding: 12px;
  background: #fff5f5;
  border: 1px solid #ff6b6b;
  border-radius: 8px;
  color: #c92a2a;
  font-size: 14px;
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 30px;
  background: #4b2e83;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(75, 46, 131, 0.3);
    background: #5a3a9a;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 16px;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #000000;
  
  svg {
    font-size: 60px;
    color: #10b981;
    margin-bottom: 20px;
  }
  
  h3 {
    color: #4b2e83;
    margin-bottom: 10px;
  }
  
  p {
    color: #000000;
  }
`;

const OfficeImages = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ImageCard = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }
  
  img {
    width: 100%;
    height: 250px;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 30px 20px 20px;
  color: white;
  
  h4 {
    margin-bottom: 5px;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
  }
`;









